import { computed, ComputedRef } from 'vue';
import { useKeycloak } from './useKeycloak';
import { useGetMyAccount } from '../hooks/user';
import { PermissionAreaType, PermissionFlagType } from '@/types/permissions';

// Roller som trumfar alla permissions
export const Role = {
    ADMIN: 'admin',
    SYSOP: 'sysop',
} as const;

type RoleType = (typeof Role)[keyof typeof Role];

/**
 * Composable för att hantera användarrättigheter baserat på Keycloak-roller
 * och bitwise permissions från API
 */
export function usePermissions() {
    const keycloak = useKeycloak();
    const { data: accountData, isLoading: isLoadingAccount } = useGetMyAccount();

    /**
     * Hämtar alla roller från Keycloak token
     * Inkluderar både realm roles och client-specifika roller
     */
    const roles: ComputedRef<string[]> = computed(() => {
        const tokenParsed = keycloak.tokenParsed;

        if (!tokenParsed) {
            return [];
        }

        const realmRoles = tokenParsed.realm_access?.roles || [];
        const clientRoles = tokenParsed.resource_access?.[keycloak.clientId || '']?.roles || [];

        // Kombinera realm och client roller
        return [...realmRoles, ...clientRoles];
    });

    const hasRole = (role: RoleType): boolean => {
        return roles.value.includes(role);
    };

    const _checkPermission = (area: PermissionAreaType, flag: PermissionFlagType): boolean => {
        const tenantSlug = accountData.value?.tenantSlug;

        if (!tenantSlug) {
            return false;
        }

        if (hasRole(Role.ADMIN) || hasRole(Role.SYSOP)) {
            return true;
        }

        const permissions = accountData.value?.access?.permissions?.effective;

        if (!permissions) {
            return false;
        }

        const areaMask = permissions[area];

        if (typeof areaMask !== 'number') {
            return false;
        }

        return (areaMask & flag) === flag;
    };

    const _permissionCache = new Map<string, ComputedRef<boolean>>();

    const hasPermission = (
        area: PermissionAreaType,
        flag: PermissionFlagType
    ): ComputedRef<boolean> => {
        const key = `${area}:${flag}`;
        if (!_permissionCache.has(key)) {
            _permissionCache.set(
                key,
                computed(() => _checkPermission(area, flag))
            );
        }
        return _permissionCache.get(key)!;
    };

    const hasAnyPermission = (
        ...checks: [PermissionAreaType, PermissionFlagType][]
    ): ComputedRef<boolean> => {
        const key = checks.map(([a, f]) => `${a}:${f}`).join('|');
        if (!_permissionCache.has(key)) {
            _permissionCache.set(
                key,
                computed(() => checks.some(([area, flag]) => _checkPermission(area, flag)))
            );
        }
        return _permissionCache.get(key)!;
    };

    return {
        roles,
        hasRole,
        hasPermission,
        hasAnyPermission,
        isLoadingAccount,
        accountData,
    };
}
